import { ObservableModelNode } from "../observable";
import type { SpecsCatalogEntry } from "../types";
import {
  createImageUrlAutoFormField,
  type SpecsAutoFormFieldMetadataMap,
} from "../autoFormMetadata";
import {
  cloneValue,
  expectObject,
  readBoolean,
  readString,
  splitKnown,
} from "../utils";
import { SpecsFabricColorCompatibilityModel } from "./SpecsFabricColorCompatibilityModel";

export class SpecsFabricColorModel extends ObservableModelNode {
  static autoFormFields: SpecsAutoFormFieldMetadataMap = {
    name: { label: "Name", fieldType: "text" },
    hex: { label: "Pastille", fieldType: "color" },
    generatorHex: { label: "Couleur calibrée", fieldType: "color" },
    label: { label: "Label", fieldType: "text" },
    fallback: { label: "Fallback", fieldType: "text" },
    textWhite: { label: "Text white", fieldType: "boolean" },
    techpackColorName: { label: "Techpack color name", fieldType: "text" },
    chartName: { label: "Chart name", fieldType: "text" },
    column: { label: "Column", fieldType: "text" },
    textureUrl: createImageUrlAutoFormField({ label: "Texture URL" }),
    disabled: { label: "Disabled", fieldType: "boolean" },
    colorEndOfLife: { label: "End of life", fieldType: "boolean" },
    migrateTo: { label: "Migrate to", fieldType: "text" },
    colorSkuId: { label: "Color SKU ID", fieldType: "text" },
    descriptionOverwrite: {
      label: "Description overwrite",
      fieldType: "text",
    },
    extra: { label: "Extra JSON", fieldType: "json", minRows: 4 },
  };

  name!: string;
  hex!: string;
  generatorHex!: string;
  label!: string;
  fallback!: string;
  textWhite!: boolean;
  techpackColorName!: string;
  chartName!: string;
  column!: string;
  textureUrl!: string;
  disabled!: boolean;
  colorEndOfLife!: boolean;
  migrateTo!: string;
  colorSkuId!: string;
  descriptionOverwrite!: string;
  colorCompatibility!: SpecsFabricColorCompatibilityModel | null;
  extra!: Record<string, unknown>;
  private hasHex = true;
  private hasGeneratorHex = true;
  private hasLabel = true;
  private hasFallback = true;
  private hasTextWhite = false;
  private hasTechpackColorName = false;
  private hasChartName = false;
  private hasColumn = false;
  private hasTextureUrl = false;
  private hasDisabled = false;
  private hasColorEndOfLife = false;
  private hasMigrateTo = false;
  private hasColorSkuId = false;
  private hasDescriptionOverwrite = false;
  private hasColorCompatibility = false;

  constructor() {
    super();
    this.defineObservableValue("name", "DEFAULT");
    this.defineObservableValue("hex", "#ffffff");
    this.defineObservableValue("generatorHex", "#ffffff");
    this.defineObservableValue("label", "Default");
    this.defineObservableValue("fallback", "#ffffff");
    this.defineObservableValue("textWhite", false);
    this.defineObservableValue("techpackColorName", "");
    this.defineObservableValue("chartName", "");
    this.defineObservableValue("column", "");
    this.defineObservableValue("textureUrl", "");
    this.defineObservableValue("disabled", false);
    this.defineObservableValue("colorEndOfLife", false);
    this.defineObservableValue("migrateTo", "");
    this.defineObservableValue("colorSkuId", "");
    this.defineObservableValue("descriptionOverwrite", "");
    this.defineObservableValue("colorCompatibility", null);
    this.defineObservableValue("extra", {});
  }

  restore(snapshot: unknown) {
    const source = expectObject(snapshot, "fabric color");
    this.hasHex = "hex" in source;
    this.hasGeneratorHex = "generatorHex" in source;
    this.hasLabel = "label" in source;
    this.hasFallback = "fallback" in source;
    this.hasTextWhite = "textWhite" in source;
    this.hasTechpackColorName = "techpackColorName" in source;
    this.hasChartName = "chartName" in source;
    this.hasColumn = "column" in source;
    this.hasTextureUrl = "textureUrl" in source;
    this.hasDisabled = "disabled" in source;
    this.hasColorEndOfLife = "colorEndOfLife" in source;
    this.hasMigrateTo = "migrateTo" in source;
    this.hasColorSkuId = "colorSkuId" in source;
    this.hasDescriptionOverwrite = "descriptionOverwrite" in source;
    this.hasColorCompatibility = "colorCompatibility" in source;
    this.name = readString(source, "name");
    this.hex = readString(source, "hex");
    this.generatorHex = readString(source, "generatorHex");
    this.label = readString(source, "label");
    this.fallback = readString(source, "fallback");
    this.textWhite = readBoolean(source, "textWhite", false);
    this.techpackColorName = readString(source, "techpackColorName");
    this.chartName = readString(source, "chartName");
    this.column = readString(source, "column");
    this.textureUrl = readString(source, "textureUrl");
    this.disabled = readBoolean(source, "disabled", false);
    this.colorEndOfLife = readBoolean(source, "colorEndOfLife", false);
    this.migrateTo = readString(source, "migrateTo");
    this.colorSkuId = readString(source, "colorSkuId");
    this.descriptionOverwrite = readString(source, "descriptionOverwrite");
    this.colorCompatibility =
      source.colorCompatibility &&
      typeof source.colorCompatibility === "object" &&
      !Array.isArray(source.colorCompatibility)
        ? new SpecsFabricColorCompatibilityModel().restore(
            source.colorCompatibility
          )
        : null;
    this.extra = splitKnown(source, [
      "name",
      "hex",
      "generatorHex",
      "label",
      "fallback",
      "textWhite",
      "techpackColorName",
      "chartName",
      "column",
      "textureUrl",
      "disabled",
      "colorEndOfLife",
      "migrateTo",
      "colorSkuId",
      "descriptionOverwrite",
      "colorCompatibility",
    ]);
    return this;
  }

  save() {
    const saved: SpecsCatalogEntry = {
      ...cloneValue(this.extra),
      name: this.name,
    };

    if (this.hasHex || this.hex) {
      saved.hex = this.hex;
    }
    if (this.hasGeneratorHex || this.generatorHex) {
      saved.generatorHex = this.generatorHex;
    }
    if (this.hasLabel || this.label) {
      saved.label = this.label;
    }
    if (this.hasFallback || this.fallback) {
      saved.fallback = this.fallback;
    }
    if (this.hasTextWhite || this.textWhite) {
      saved.textWhite = this.textWhite;
    }
    if (this.hasTechpackColorName || this.techpackColorName) {
      saved.techpackColorName = this.techpackColorName;
    }
    if (this.hasChartName || this.chartName) {
      saved.chartName = this.chartName;
    }
    if (this.hasColumn || this.column) {
      saved.column = this.column;
    }
    if (this.hasTextureUrl || this.textureUrl) {
      saved.textureUrl = this.textureUrl;
    }
    if (this.hasDisabled || this.disabled) {
      saved.disabled = this.disabled;
    }
    if (this.hasColorEndOfLife || this.colorEndOfLife) {
      saved.colorEndOfLife = this.colorEndOfLife;
    }
    if (this.hasMigrateTo || this.migrateTo) {
      saved.migrateTo = this.migrateTo;
    }
    if (this.hasColorSkuId || this.colorSkuId) {
      saved.colorSkuId = this.colorSkuId;
    }
    if (this.hasDescriptionOverwrite || this.descriptionOverwrite) {
      saved.descriptionOverwrite = this.descriptionOverwrite;
    }
    if (this.hasColorCompatibility || this.colorCompatibility) {
      saved.colorCompatibility = this.colorCompatibility?.save() ?? null;
    }

    return saved;
  }
}
